import { FocusMonitor } from "@angular/cdk/a11y";
import { BreakpointObserver } from "@angular/cdk/layout";
import {
	AfterContentInit,
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output,
} from "@angular/core";
import { FormControl, FormGroup, UntypedFormGroup, Validators } from "@angular/forms";
import { MatLegacyDialog as MatDialog } from "@angular/material/legacy-dialog";
import { ElRefDirective } from "@app/_directives/el-ref/el-ref.directive";
import { resolveClientRefDisplayColor } from "@app/_helpers/client-color";
import { resolveTagDisplayColor } from "@app/_helpers/tag-color";
import { getTimesLockedDate, isTimeLockedForDate, isTimeLockedForDateFuture } from "@app/_helpers/timeLock";
import { createRxValue, distinctUntilChangedJson } from "@app/_helpers/utils";
import startWithDefault from "@app/_operators/startWithDefault";
import { CustomValidators } from "@app/_validators/custom-validators";
import { AppService } from "@app/app.service";
import {
	parseDurationAsFormat,
	parseDurationSeconds,
} from "@app/components/duration-input-control/duration-input-utils";
import { DeletionUndoService } from "@app/services/deletion/deletion-undo.service";
import { EditUndoService } from "@app/services/edit-undo/edit-undo.service";
import { MixedTimesSyncService } from "@app/services/mixed-times-sync.service";
import {
	ClientProjectPickerStepperData,
	ClientProjectPickerStepperDialogComponent,
} from "@app/shared/dialogs/client-project-picker-stepper-dialog/client-project-picker-stepper-dialog.component";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { TranslateService } from "@ngx-translate/core";
import {
	ClientsQuery,
	Logger,
	MyTimesService,
	Project,
	ProjectsQuery,
	Tag,
	TagsQuery,
	TagType,
	Task,
	TasksService,
	Time,
	UserSettingsQuery,
} from "@timeghost/web-api";
import { differenceInDays, endOfDay, intervalToDuration, isAfter, isValid, parse, startOfDay } from "date-fns/esm";
import { format } from "date-fns-tz/esm/fp";
import { flow, xorBy } from "lodash-es";
import { combineLatest, defer, EMPTY, of } from "rxjs";
import { catchError, debounceTime, filter, finalize, map, startWith, switchMap, tap } from "rxjs/operators";
import { TagDialogData, TagPickerDialogComponent } from "../dialogs/tag-picker-dialog/tag-picker-dialog.component";
import { SelectMode } from "../record-toolbar/record-toolbar.component";
import { RecordToolbarService } from "../record-toolbar/record-toolbar.service";
import { TimeDatePickerComponent } from "../time-date-picker/time-date-picker.component";
import { TimeDatePickerConfig } from "../time-date-picker/time-date-picker-config";
import { TimeTrackCreateData } from "../time-tracker-calendar-create-dialog/time-tracker-calendar-create-dialog.component";
import { TimeTrackerCalendarStepperCreateDialogComponent } from "../time-tracker-calendar-stepper-create-dialog/time-tracker-calendar-stepper-create-dialog.component";
import { TimeTrackerCalendarUpdateDialogComponent } from "../time-tracker-calendar-update-dialog/time-tracker-calendar-update-dialog.component";

const log = new Logger("TimeTrackerEntryComponent");
const createTimeValidators = () => [Validators.required, CustomValidators.validDateFormat("HH:mm")];
const disabledFlags = ["comego"];

@UntilDestroy()
@Component({
	selector: "app-time-tracker-entry",
	templateUrl: "./time-tracker-entry.component.html",
	styleUrls: ["./time-tracker-entry.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	host: {
		class: "time-entry-host",
	},
})
export class TimeTrackerEntryComponent implements OnInit, AfterContentInit, AfterViewInit, OnDestroy {
	isMobile$ = this.appService.isMobile$;
	readonly isBelow1100$ = this.bp.observe("(max-width: 1099px)").pipe(
		map((x) => x.matches),
		untilDestroyed(this)
	);
	group = new FormGroup({
		name: new FormControl<string>(null),
		project: new FormControl(null, [Validators.required]),
		tags: new FormControl([]),
		billable: new FormControl(false),
		task: new FormControl(null),
		time: new FormGroup(
			{
				start: new FormControl("00:00"),
				end: new FormControl("00:00"),
				duration: new FormControl("00:00"),
			},
			{
				updateOn: "blur",
			}
		),
	});
	groupValue$ = combineLatest([
		this.group.valueChanges.pipe(startWith(this.group.getRawValue())),
		this.group.statusChanges.pipe(startWithDefault()),
	]).pipe(
		untilDestroyed(this),
		debounceTime(50),
		map(([v]) => v)
	);

	/** Labels from the saved time entry (server data), not unsaved form edits. */
	get selectedTimeEntryTags(): Tag[] {
		return (this.entry?.tags ?? []) as Tag[];
	}

	private _stateVisibleMode = createRxValue(false);
	public get stateVisibleMode(): boolean {
		return this._stateVisibleMode.value;
	}
	public set stateVisibleMode(v: boolean) {
		this._stateVisibleMode.value = v;
	}
	public stateVisibleMode$ = this._stateVisibleMode.asObservable();

	toggleStateVisibleMode() {
		const v = this.stateVisibleMode;
		this.stateVisibleMode = !v;
		if (this.mode === "range_optional" && v) {
			(this.group.get("time") as UntypedFormGroup).patchValue({
				start: "00:00",
				end: "00:00",
			});
			this.group.updateValueAndValidity();
		}
		this.cdref.detectChanges();
	}
	private _entry = createRxValue<Time>(null);
	readonly entry$ = this._entry.asObservable().pipe(distinctUntilChangedJson(), untilDestroyed(this));
	/** Recomputes when form, entry, or global tag store changes. */
	readonly tagTooltipData$ = combineLatest([
		this.entry$,
		this.group.valueChanges.pipe(
			startWith(this.group.getRawValue()),
			map(() => this.group.getRawValue())
		),
		this.tagsQuery.selectAll().pipe(startWith(this.tagsQuery.getAll())),
	]).pipe(
		untilDestroyed(this),
		map(([entry, formValue]) => {
			const projectTags = this.resolveTags(
				(((formValue?.project as Project | null) ?? entry?.project)?.tags ?? []) as Tag[]
			);
			const tags = this.resolveTags((formValue?.tags ?? []) as Tag[]);
			const allTags = [...projectTags, ...tags]
				.uniqBy((t) => t.id)
				.sort((a, b) => (a?.name ?? "").localeCompare(b?.name ?? "", undefined, { sensitivity: "base" }));
			return {
				allTags,
				hasTags: allTags.length > 0,
			};
		})
	);
	readonly entry$disabled = this.entry$.pipe(
		map((x) => !!x.currentUserCanEdit),
		untilDestroyed(this)
	);
	readonly entry$timeDiff = this.entry$.pipe(
		map((x) => ~~x?.timeDiff),
		untilDestroyed(this)
	);
	get entry() {
		return this._entry.value;
	}
	@Input()
	set entry(val: Time) {
		this._entry.next(val);
		this.prepareForm(val);
		this.cdref.markForCheck();
	}
	get timeDiff() {
		return ~~this.entry?.timeDiff;
	}
	get mode() {
		return this.userSettingsQuery.getValue()?.workspace.settings?.timesMode;
	}

	private _isLoading: boolean;
	public get isLoading(): boolean {
		return this._isLoading;
	}
	public set isLoading(v: boolean) {
		this._isLoading = v;
		this.group[v ? "disable" : "enable"]();
		this.cdref.markForCheck();
	}
	readonly projectView$ = combineLatest([
		this.entry$.pipe(startWith(this.entry)),
		this.group.valueChanges.pipe(startWith(this.group.getRawValue())),
	]).pipe(
		switchMap(([entry, formValue]) => {
			const project = formValue?.project || entry?.project;
			const task = formValue?.task ? formValue.task : entry?.task;
			if (!project?.id) return of(null);
			return this.projectsQuery.selectEntity(project.id).pipe(
				map((storeProject) => ({
					project: storeProject ? { ...project, ...storeProject } : project,
					task,
				})),
				startWith({ project, task })
			);
		}),
		untilDestroyed(this)
	);
	constructor(
		private ref: ElementRef,
		private myTimesService: MyTimesService,
		private projectsQuery: ProjectsQuery,
		private clientsQuery: ClientsQuery,
		private tagsQuery: TagsQuery,
		private i18nService: TranslateService,
		private appService: AppService,
		private userSettingsQuery: UserSettingsQuery,
		private cdref: ChangeDetectorRef,
		private recordService: RecordToolbarService,
		private dialog: MatDialog,
		private fm: FocusMonitor,
		private deletionService: DeletionUndoService,
		private editUndoService: EditUndoService,
		private tasksService: TasksService,
		private bp: BreakpointObserver,
		private mixedTimesSyncService: MixedTimesSyncService
	) {}
	forceFocus = new EventEmitter(true);
	ngOnInit() {
		if (this.isTimeLocked(this.entry)) {
			this.group.disable();
		}
		this.prepareForm(this.entry);

		// Subscribe to deletion events from service
		this.deletionService.deleted
			.pipe(
				filter((ev) => ev.type === "timeEntry" && ev.key === this.entry?.id),
				untilDestroyed(this)
			)
			.subscribe((ev) => {
				this.Deleted?.next(ev.key);
				this.Deleted?.complete();
			});

		combineLatest([
			this.fm.monitor(this.ref, true).pipe(debounceTime(500)),
			this.groupValue$.pipe(distinctUntilChangedJson()),
			this.forceFocus.asObservable().pipe(
				startWith(Date.now() - 1000),
				map((x) => (x ? x : Date.now() + 1000)),
				distinctUntilChangedJson()
			),
		])
			.pipe(
				untilDestroyed(this),
				filter(
					([focus, data, force]) =>
						((force && force > Date.now()) || focus === null) &&
						!this.isLoading &&
						this.group.valid &&
						this.dialog.openDialogs.length === 0
				),
				distinctUntilChangedJson(([, value]) => value),
				debounceTime(100),
				switchMap(([, value]) =>
					defer(() => this.updateTime(value)).pipe(
						catchError((err) => {
							this.isLoading = false;
							this.prepareForm(this.entry);
							log.error(err);
							return EMPTY; // important to return EMPTY to avoid errors
						})
					)
				),
				filter((x): x is { original: Time; updated: Time } => !!x?.updated)
			)
			.subscribe({
				next: ({ original, updated }) => {
					this.mixedTimesSyncService.handleProjectTimeDialogClose(updated, original);
					this.editUndoService.offerSingleUndo({ original, updated });
					log.debug(updated);
					this.isLoading = false;
					this.prepareForm(updated);
				},
				error(err) {
					log.error(err);
				},
			});
		this.entry$disabled.subscribe((x) => {
			if (x !== this.group.disabled) this.group[!x ? "disable" : "enable"]();
		});
	}
	ngAfterContentInit() {}
	ngAfterViewInit(): void {}
	ngOnDestroy(): void {}
	prepareForm(x: Time) {
		const mode = this.mode;
		const formStart = Date.parse(x.start);
		let formEnd = Date.parse(x.end);
		if (differenceInDays(formStart, formEnd) >= 1) formEnd = endOfDay(formStart).getTime();
		const { start, end, duration } = {
			start: flow(format("HH:mm"))(formStart) as string,
			end: flow(format("HH:mm"))(formEnd) as string,
			duration: parseDurationAsFormat(x.timeDiff / 60),
		};
		if (mode === "range_optional") this._stateVisibleMode.value = x.inputMode !== "duration";
		this.group.patchValue(
			{
				name: x.name,
				project: x.project,
				task: x.task,
				tags: x.tags || [],
				billable: !!x.billable,
				time: {
					start,
					end,
					duration,
				},
			},
			{ emitEvent: false }
		);
		((group: UntypedFormGroup) => {
			if (mode !== "range") {
				group.controls.start.clearValidators(),
					group.controls.end.clearValidators(),
					group.controls.duration.clearValidators();
			} else {
				group.controls.start.setValidators([Validators.required, CustomValidators.validDateFormat("HH:mm")]),
					group.controls.end.setValidators([Validators.required, CustomValidators.validDateFormat("HH:mm")]),
					group.controls.duration.setValidators([Validators.required, CustomValidators.validDuration()]);
			}
		})(this.group.controls.time as UntypedFormGroup);
		this.group.setMustChange();
		this.group.updateValueAndValidity();
		if (x.currentUserCanEdit === false) {
			this.group.disable();
		}
		this.cdref.detectChanges();
	}
	updateTime(value: any) {
		this.isLoading = true;
		const originalSnapshot = structuredClone(this.entry);
		const refDate = new Date(this.entry.start);
		try {
			const [newStart, newEnd] = [value.time.start, value.time.end].map((d) => parse(d, "HH:mm", refDate));
			const mode = this.mode;
			const inputMode = mode === "range_optional" && !this.stateVisibleMode ? "duration" : "range";
			if (mode !== "duration" && inputMode !== "duration" && newEnd <= newStart) {
				this.appService.notifyError("errors.record.time-invalid-range");
				this.isLoading = false;
				this.prepareForm(this.entry);
				return Promise.resolve(null);
			}
			if (mode !== "duration") {
				let _start = newStart,
					_end = newEnd;
				let start = new Date(new Date(refDate).setHours(_start.getHours(), _start.getMinutes(), 0, 0)),
					end = new Date(new Date(refDate).setHours(_end.getHours(), _end.getMinutes(), 0, 0));
				if (end < start) {
					end = new Date(start.getTime());
				}
				if ([start, end].some((x) => !isValid(x))) {
					throw new Error("Invalid Start / End Values");
				}
				if (isAfter(start, end)) throw new Error("Invalid Start / End Values");

				const project = this.projectsQuery.getAll().find((x) => x.id == this.entry.project.id);
				const userSettings = this.userSettingsQuery.getValue();
				const blockTimes = userSettings.workspace.settings?.blockTimes;
				if (isTimeLockedForDate(userSettings, project, start)) {
					this.appService.notifier.show({
						message: this.i18nService.instant("errors.blockTimesPast", {
							value: blockTimes?.period,
							range: blockTimes?.range,
						}),
						type: "error",
					});
					throw new Error();
				}
				if (isTimeLockedForDateFuture(this.userSettingsQuery.getValue(), project, end)) {
					this.appService.notifier.show({
						message: this.i18nService.instant("errors.blockTimesFuture", {
							value: blockTimes?.futurePeriod,
							range: blockTimes?.futureRange,
						}),
						type: "error",
					});
					throw new Error();
				}
				const nextDuration =
					inputMode === "duration"
						? parseDurationSeconds(value.time.duration)
						: (({ hours, minutes }) => hours * 60 + minutes)(intervalToDuration({ start, end })) * 60;
				if (inputMode === "duration")
					log.debug("nextDuration", {
						startOf: startOfDay(start.getTime()).toISOString(),
						timeDiff: nextDuration,
					});
				return new Promise<{ original: Time; updated: Time }>((resolve, reject) =>
					this.myTimesService
						.update({
							...this.entry,
							name: value.name,
							tags: value.tags || [],
							billable: !!value.billable,
							project: value.project,
							// @ts-ignore
							inputMode,
							...(inputMode === "duration"
								? {
										start: startOfDay(start.getTime()).toISOString(),
										end: startOfDay(start.getTime()).toISOString(),
										timeDiff: nextDuration,
									}
								: { start: start.toISOString(), end: end.toISOString() }),
							task: value.task,
						})
						.pipe(finalize(() => (this.isLoading = false)))
						.subscribe({
							next: ([x]) => {
								this.recordService.handleSuccess(x);
								resolve({ original: originalSnapshot, updated: x });
							},
							error: (err) => {
								this.recordService.handleError(err);
								reject(err);
							},
						})
				);
			} else {
				const newDuration = parseDurationSeconds(value.time.duration);
				return new Promise<{ original: Time; updated: Time }>((resolve, reject) =>
					this.myTimesService
						.update({
							...this.entry,
							name: value.name,
							tags: value.tags || [],
							billable: !!value.billable,
							project: value.project,
							timeDiff: newDuration,
							task: value.task,
						})
						.pipe(finalize(() => (this.isLoading = false)))
						.subscribe({
							next: ([x]) => {
								this.recordService.handleSuccess(x);
								resolve({ original: originalSnapshot, updated: x });
							},
							error: (err) => {
								this.recordService.handleError(err);
								reject(err);
							},
						})
				);
			}
		} catch (err) {
			this.isLoading = false;
			return Promise.reject(err);
		}
	}
	get isManualMode() {
		return this.recordService.selectedRecordMode === SelectMode.Manual;
	}
	readonly isManualMode$ = this.recordService.selectedRecordMode$.pipe(map((x) => x === SelectMode.Manual));
	getProjectColor(id: string) {
		return this.projectsQuery.getEntity(id)?.color;
	}
	isDefaultProject(id: string) {
		return this.projectsQuery.getEntity(id)?.useAsDefault === true;
	}
	@Output()
	Deleted = new EventEmitter<string>(true);
	@Output()
	DateUpdate = new EventEmitter<string>(true);

	get isPendingDeletion(): boolean {
		return this.deletionService.isPendingDeletion(this.entry?.id);
	}

	deleteEntry(entry: Time) {
		this.deletionService.deleteWithUndo({ type: "timeEntry", entry });
	}

	selectInput(ev: Event, timeInput: ElRefDirective) {
		const el: HTMLElement = timeInput.elementRef.nativeElement;
		if (!el) {
			return;
		}
		const input = el.querySelector("input");
		if (input) {
			ev.preventDefault();
			input.select();
		}
	}
	private resolveTag(tag: Tag | null | undefined): Tag | null {
		if (!tag?.id) return tag ?? null;
		return (this.tagsQuery.getEntity(tag.id) as Tag & { color?: string | null }) ?? tag;
	}

	private resolveTags(tags: Tag[]): Tag[] {
		return tags.map((t) => this.resolveTag(t)).filter((t): t is Tag => !!t);
	}

	openTagPicker() {
		if (this.group.disabled) return;
		const entry = this.entry;
		const projectTags = this.resolveTags(
			(((this.group.getRawValue().project as Project | null) ?? entry?.project)?.tags ?? []) as Tag[]
		);
		const lockedTagIds = projectTags.map((x) => x.id);
		const selectedEditableTags = this.resolveTags((this.group.getRawValue().tags ?? entry?.tags ?? []) as Tag[]);
		const selectedTags = [...projectTags, ...selectedEditableTags].uniqBy((x) => x.id);
		this.dialog
			.open(TagPickerDialogComponent, {
				data: <TagDialogData>{
					data: {
						SelectedTags: selectedTags,
						LockedTagIds: lockedTagIds,
						canToggle: true,
						type: TagType.Time,
						allowTypeModify: false,
					},
				},
				restoreFocus: false,
			})
			.afterClosed()
			.pipe(map((x) => x || []))
			.subscribe((x) => {
				// keep project tags visible/locked in the picker, but don't store them on the time entry
				const editable = this.resolveTags((x as Tag[]).filter((t) => !lockedTagIds.includes(t.id)));
				const current = (this.group.getRawValue().tags ?? []) as Tag[];
				// Dialog returns spread copies (e.g. tagType); patching would change JSON and retrigger save
				if (xorBy(current, editable, (t) => t.id).length === 0) return;
				this.group.patchValue({
					tags: [...editable],
				});
				this.cdref.markForCheck();
			});
	}
	toggleBilled() {
		if (this.group.disabled) return;
		this.group.patchValue({
			billable: !this.group.value.billable,
		});
		setTimeout(() => {
			this.forceFocus.emit();
		}, 100);
	}
	readonly projectTooltipData$ = this.entry$.pipe(
		map((val) => {
			return {
				project: val.project,
				client: val.client,
				task: val.task,
				tags: val.project?.tags,
			};
		})
	);
	get timeFormat() {
		return this.appService.timeFormat;
	}
	get timeZone() {
		return this.userSettingsQuery.getValue().settings.timeZone;
	}
	get currentLang() {
		return this.i18nService.currentLang;
	}
	async copyEntry(time: Time) {
		if (time.project.completed) return;
		if (this.appService.isMobile) {
			this.dialog.open(TimeTrackerCalendarStepperCreateDialogComponent, {
				data: <TimeTrackCreateData>{
					billable: time.billable,
					title: time.name,
					project: time.project,
					task: time.task,
					tags: time.tags,
				},
			});
		} else {
			this.recordService.copyRecording(time);
		}
		const project = this.projectsQuery.getEntity(time.project.id);
		const task = await this.tasksService.getById(time.task.id).toPromise();
		const totalTimeProject = project.timesRecorded
			? (project.timesRecorded.billable?.clamp(0, project.timesRecorded.billable) || 0) +
				(!project.billable
					? project.timesRecorded.notBillable?.clamp(0, project.timesRecorded.notBillable) || 0
					: 0)
			: 0;
		const totalTimeTask = (task.timesRecorded.billable ?? 0) + (task.timesRecorded.notBillable ?? 0);
		if (
			(task.estimation && totalTimeTask >= task.estimation) ||
			(project.estimation && totalTimeProject >= project.estimation)
		) {
			this.appService.notifyInfo(this.i18nService.instant("project.budget.limitReached"));
		}
	}
	continueEntry(time: Time) {
		return this.recordService.continueRecording(time);
	}
	private dialogOpen = false;
	openProjectPicker(time: Time) {
		if (this.group.disabled || this.isLoading) return;
		const { project, task } = this.group.value;
		this.dialogOpen = true;
		this.dialog
			.open(ClientProjectPickerStepperDialogComponent, {
				data: <ClientProjectPickerStepperData>{
					skipProjectStepper: !this.isDefaultProject(this.group.value.project.id) ? true : false,
					project: this.group.value.project,
					task: this.group.value.task,
					userId: time.user?.id,
				},
			})
			.afterClosed()
			.pipe(tap(() => (this.dialogOpen = false)))
			.pipe(filter((x) => !!x))
			.pipe(filter(([x, t]: [Project, Task]) => !!x && (x.id !== project.id || t?.id !== task?.id)))
			.subscribe(([x, t]) => {
				if (isTimeLockedForDate(this.userSettingsQuery.getValue(), x, new Date(time.start))) {
					this.appService.notifyError("errors.blockProjectChange");
					return;
				}
				if (isTimeLockedForDateFuture(this.userSettingsQuery.getValue(), x, new Date(time.end))) {
					this.appService.notifyError("errors.blockProjectChangeF");
					return;
				}
				if (this.group.value.project.id != x.id) {
					this.group.patchValue({ project: x, task: t });
				}
				if (t) {
					this.group.patchValue({ task: t });
				}
			});
	}
	editTime(time: Time) {
		this.dialog.open(TimeTrackerCalendarUpdateDialogComponent, {
			data: {
				title: time.name,
				start: new Date(time.start),
				end: new Date(time.end),
				meta: { time },
			},
		});
	}
	allowedDate() {
		const projectId = this.group.value.project.id;
		let project = this.projectsQuery.getAll().find((x) => x.id == projectId);
		return getTimesLockedDate(this.userSettingsQuery.getValue(), project);
	}
	trackId(_index: number, item: { id: string }) {
		return item.id;
	}

	tagDisplayColor(tag: Tag & { color?: string | null }) {
		return resolveTagDisplayColor(tag.color, tag.id, (n) => this.appService.colorScheme.getColorFromScheme(n));
	}

	isTimeLocked(entry: Time) {
		const projectId = this.group.value.project.id;
		let project = this.projectsQuery.getAll().find((x) => x.id == projectId);
		const usersettings = this.userSettingsQuery.getValue();
		const date = new Date(entry.start);
		const isLocked =
			isTimeLockedForDate(usersettings, project, date) ||
			isTimeLockedForDateFuture(usersettings, project, new Date(entry.end));
		return isLocked;
	}

	openCalPicker(time: Time) {
		const originalEntry = structuredClone(time);
		return this.dialog
			.open(TimeDatePickerComponent, {
				data: <TimeDatePickerConfig>{
					selectedDate: new Date(time.start),
					minDate: new Date(this.allowedDate()),
				},
			})
			.afterClosed()
			.pipe<Date, any>(
				filter((x) => isValid(x)),
				switchMap((x) => {
					const start = new Date(time.start),
						end = new Date(time.end);
					const newStart = new Date(
							x.setHours(
								start.getHours(),
								start.getMinutes(),
								start.getSeconds(),
								start.getMilliseconds()
							)
						),
						newEnd = new Date(
							x.setHours(end.getHours(), end.getMinutes(), end.getSeconds(), end.getMilliseconds())
						);
					return defer(() => {
						this.isLoading = true;
						return this.myTimesService
							.update({ ...time, start: newStart.toISOString(), end: newEnd.toISOString() })
							.pipe(finalize(() => (this.isLoading = false)));
					});
				})
			)
			.subscribe(
				([x]) => {
					this.mixedTimesSyncService.handleProjectTimeDialogClose(x, originalEntry);
					this.recordService.handleSuccess(x);
				},
				(err) => this.recordService.handleError(err)
			);
	}
	clientDisplayColor(client: { id?: string; color?: string | null } | null | undefined) {
		return resolveClientRefDisplayColor(
			client,
			(id) => this.clientsQuery.getEntity(id),
			(n) => this.appService.colorScheme.getColorFromScheme(n)
		);
	}
}
